import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from "react-modal";
import { useForm } from 'react-hook-form';
import useLoading from '../../../../hooks/useLoading';
import { roomCustomerApi } from '../../../../apis/admin/room/roomCustomerApi';
import { Member, Customer } from '../../../../types/admin/room';
import {ApiClientError} from "../../../../apis/errors";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import useAppRouter from "../../../../hooks/useAppRouter";
import {logError} from "../../../../libs/utils/logError";

interface SelectOwnerModalProps {
  onSubmitted: (isChange: boolean, salesman: Member | undefined) => Promise<void>;
}

interface FormValuesOfOwnerEmail {
  ownerEmail: string;
}

interface FormValuesOfCreateUser {
  userName: string;
  email: string;
  companyName: string;
  department: string;
  position: string;
}



export const SelectOwnerModal = (props: SelectOwnerModalProps) => {
  const { onSubmitted } = props;

  const withLoading = useLoading();
  const location = useLocation();

  const {
    register: searchFormRegister,
    formState: { errors: searchFormErrors, },
    handleSubmit: searchFormHandleSubmit
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const {
    register: createUserRegister,
    formState: { errors: createUserErrors, },
    handleSubmit: createUserHandleSubmit,
    setValue: createUserSetValue,
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [showOwnerSearch, setShowOwnerSearch] = React.useState<boolean>(true);
  const [showOwnerResult, setShowOwnerResult] = React.useState<boolean>(false);
  const [showOwnerResultNone, setShowOwnerResultNone] = React.useState<boolean>(false);

  const [customer, setCustomer] = React.useState<Customer>();

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();

  const handleUserSearchClick = () => {
    withLoading(async () => {
      setApiClientError(null);

      await searchFormHandleSubmit(
        async (data: FormValuesOfOwnerEmail) => {
          try {
            const response = await roomCustomerApi.search({email: data.ownerEmail}, {});

            setShowOwnerSearch(false);
            if (response.isExist) {
              setCustomer(response.customer);
              setShowOwnerResult(true);
            } else {
              setShowOwnerResultNone(true);
              createUserSetValue('email', data.ownerEmail);
            }

          } catch (e) {
            await logError(
              location.pathname,
              '顧客ユーザーの検索に失敗しました。',
              e
            );

            if (e instanceof ApiClientError) {
              setApiClientError(e);
              return;
            }

            goto('internalServerError');
          }
        }
      )();
    })};

  const handleCreateOwnerClick = async () => {
    withLoading(async () => {
      setApiClientError(null);

      await createUserHandleSubmit(async (data: FormValuesOfCreateUser) => {
        try {
          const response = await roomCustomerApi.create(data, {});

          await onSubmitted(true, {
            userId: response.customer.userId,
            userName: response.customer.userName,
          });

        } catch (e) {
          await logError(
            location.pathname,
            '顧客ユーザーの作成に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  const add = () => {
    if (customer === undefined) {
      return;
    }

    onSubmitted(true, {
      userId: customer.userId,
      userName: customer.userName,
    });
  }


  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted(false, undefined)}
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
        className="form-modal"
      >
        <section className="modal-background">
          <div className="form-modal-container">
            <div className="form-modal-title">
              <h1 className="font-semibold">オーナー追加</h1>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => onSubmitted(false, undefined)}
              >
                ×
              </button>
            </div>
            

            { showOwnerSearch && (
            <form id="searchUserForm">
              <div className="form-modal-content">
                <div className="text-danger text-xs mb-4">
                  { apiClientError?.message }
                  { (Object.keys(searchFormErrors).length > 0) && '入力エラーがあります' }
                </div>

                <div className="grid grid-cols-1 w-full">
                  <label className="block" htmlFor="ownerEmail">
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...searchFormRegister('ownerEmail',
                        makeRegisterOptions('メールアドレス', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                          { type: FormValidationType.EMAIL, typeName: 'email' },
                        ])
                      )}
                      className="text-field"
                      id="ownerEmail"
                      type="text"
                      placeholder="検索するオーナーのメールアドレスを入力してください"
                      maxLength={86}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {searchFormErrors.ownerEmail?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'email',
                      }}
                    />
                  </label>
                </div>
              </div>
            </form>
            )}

            { showOwnerResult && (
            <div className='form-modal-content'>
              <div className="grid grid-cols-1 w-full">
                <span className="text-sm font-semibold text-primary mb-4">下記のユーザがヒットしました。データを確認して追加してください。</span>
                <table className="w-full table-fixed mt-2">
                  <tbody className="text-sm">
                    <tr>
                      <td>お名前</td>
                      <td>{ customer?.userName }</td>
                    </tr>
                    <tr>
                      <td>メールアドレス</td>
                      <td>{ customer?.email }</td>
                    </tr>
                    <tr>
                      <td>会社名</td>
                      <td>{ customer?.companyName }</td>
                    </tr>
                    <tr>
                      <td>部署名</td>
                      <td>{ customer?.department }</td>
                    </tr>
                    <tr>
                      <td>役職</td>
                      <td>{ customer?.position }</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            )}

            { showOwnerResultNone && (
            <form id="createUserForm">
              <div className="form-modal-content overflow-y-auto" style={{ flexDirection: 'unset', height: '30rem' }}>
                <div className="grid grid-cols-1 w-full">
                  <span className="text-sm font-semibold text-primary mb-2">未登録の顧客です。データを新規に登録してください。</span>
                  <div className="text-danger text-xs mb-4">
                    { apiClientError?.message }
                    { (Object.keys(createUserErrors).length > 0) && '入力エラーがあります' }
                  </div>
                  <label className="block mt-4" htmlFor="userName">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>お名前</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...createUserRegister('userName',
                        makeRegisterOptions('お名前', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                        ])
                      )}
                      id="userName"
                      className="text-field"
                      type="text"
                      maxLength={100}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {createUserErrors.userName?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'userName',
                      }}
                    />
                  </label>

                  <label className="block mt-4" htmlFor="email">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>メールアドレス</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...createUserRegister('email',
                        makeRegisterOptions('メールアドレス', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                          { type: FormValidationType.EMAIL, typeName: 'email' },
                        ])
                      )}
                      id="email"
                      className="text-field"
                      type="text"
                      maxLength={86}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {createUserErrors.email?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'email',
                      }}
                    />
                  </label>

                  <label className="block mt-4" htmlFor="companyName">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>会社名</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...createUserRegister('companyName',
                        makeRegisterOptions('会社名', [
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                        ])
                      )}
                      className="text-field"
                      id="companyName"
                      type="text"
                      maxLength={100}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {createUserErrors.companyName?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'companyName',
                      }}
                    />
                  </label>

                  <label className="block mt-4" htmlFor="department">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>部署名</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...createUserRegister('department',
                        makeRegisterOptions('部署名', [
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                        ])
                      )}
                      className="text-field"
                      id="department"
                      type="text"
                      maxLength={100}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {createUserErrors.department?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'department',
                      }}
                    />
                  </label>

                  <label className="block mt-4" htmlFor="position">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>役職</span>
                    </div>
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...createUserRegister('position',
                        makeRegisterOptions('役職', [
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                        ])
                      )}
                      className="text-field"
                      id="position"
                      type="text"
                      maxLength={100}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {createUserErrors.position?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'position',
                      }}
                    />
                  </label>
                </div>
              </div>
            </form>
            )}

            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="float-left btn-sub"
                  type="button"
                  onClick={() => onSubmitted(false, undefined)}
                  >
                  キャンセル
                </button>

                { showOwnerSearch && (
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={handleUserSearchClick}
                    >
                    検 索
                  </button>
                )}

                { showOwnerResult && (
                  <button
                    className="float-right btn-primary"
                    type="button"
                    onClick={() => add()}
                    >
                    オーナー追加
                  </button>
                )}

                { showOwnerResultNone && (
                  <button
                    className="float-right btn-primary"
                    type="button"
                    onClick={handleCreateOwnerClick}
                    >
                    オーナー追加
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </ReactModal>
    </>
  );
};
